/**
 * Onboarding Step Drill-Down Page
 *
 * Shows all orgs at a specific setup step.
 * Reached via clicking a step bar in the org funnel chart.
 */

import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { founderDashboardAPI } from '../../../services/api';
import { useEnvironment } from '../../../context/EnvironmentContext';
import DataTable from '../../../components/founder-dashboard/DataTable';
import RefreshIndicator from '../../../components/founder-dashboard/RefreshIndicator';
import {
  ArrowLeftIcon,
  BuildingOffice2Icon,
  ChevronRightIcon,
} from '@heroicons/react/24/outline';

const STEP_LABELS = {
  org_created: 'Organization Created',
  email_verified: 'Email Verified',
  first_login: 'First Login',
  campus_added: 'Campus Added',
  first_group: 'First Group Created',
  second_admin: 'Second Admin Invited',
  members_invited: 'Members Invited',
  join_codes: 'Join Codes Active',
  first_prayer: 'First Prayer',
  first_chat: 'First Chat Message',
  first_event: 'First Event',
  first_announcement: 'First Announcement',
};

// Ordered steps for determining "what's next"
const STEP_ORDER = [
  'org_created',
  'email_verified',
  'first_login',
  'campus_added',
  'first_group',
  'second_admin',
  'members_invited',
  'join_codes',
  'first_prayer',
  'first_chat',
  'first_event',
  'first_announcement',
];

function getNextStep(currentStep) {
  const idx = STEP_ORDER.indexOf(currentStep);
  if (idx === -1 || idx >= STEP_ORDER.length - 1) return null;
  return STEP_ORDER[idx + 1];
}

export default function OnboardingStepDrillDown() {
  const { currentEnv } = useEnvironment();
  const { step } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dataEnv, setDataEnv] = useState(null);

  const stepLabel = STEP_LABELS[step] || step;
  const nextStep = getNextStep(step);
  const nextStepLabel = nextStep ? STEP_LABELS[nextStep] : null;

  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await founderDashboardAPI.getOnboardingStepOrgs(step);
      setData(response.data);
      setDataEnv(currentEnv);
    } catch (err) {
      console.error('[OnboardingStepDrillDown] Error:', err);
      const errorData = err.response?.data?.error;
      const errorMsg =
        typeof errorData === 'string'
          ? errorData
          : errorData?.message || err.message || 'Couldn\'t load organizations. Try again?';
      setError(errorMsg);
      setDataEnv(currentEnv);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setData(null);
    setError(null);
    fetchData();
  }, [step, currentEnv]);

  const columns = [
    {
      key: 'name',
      header: 'Organization',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
            <BuildingOffice2Icon className="w-4 h-4 text-indigo-600" />
          </div>
          <div>
            <p className="font-medium text-gray-900">{value || 'Unnamed'}</p>
            {(row.city || row.state) && (
              <p className="text-xs text-gray-500">
                {[row.city, row.state].filter(Boolean).join(', ')}
              </p>
            )}
          </div>
        </div>
      ),
    },
    {
      key: 'memberCount',
      header: 'Members',
      sortable: true,
      render: (value) => (
        <span className="font-medium text-gray-900">{value ?? 0}</span>
      ),
    },
    {
      key: 'createdAt',
      header: 'Created',
      sortable: true,
      render: (value) =>
        value ? new Date(value).toLocaleDateString() : '-',
    },
    {
      key: 'nextStep',
      header: 'What\'s Missing',
      render: (value) => {
        const label = value ? STEP_LABELS[value] || value : nextStepLabel;
        if (!label) return <span className="text-green-600 font-medium">All done</span>;
        return (
          <span className="px-2 py-1 rounded-full text-xs font-medium bg-amber-100 text-amber-800">
            Needs: {label}
          </span>
        );
      },
    },
    {
      key: 'tier',
      header: 'Tier',
      render: (value) => (
        <span className="px-2 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 capitalize">
          {value || 'Free'}
        </span>
      ),
    },
  ];

  // Error state
  if (error && dataEnv === currentEnv) {
    return (
      <div className="space-y-4">
        <button
          onClick={() => navigate('/onboarding/funnels')}
          className="flex items-center gap-2 text-gray-600 hover:text-gray-900 min-h-[44px]"
        >
          <ArrowLeftIcon className="w-5 h-5" />
          Back to Funnels
        </button>
        <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
          <p className="text-red-800 font-medium">Couldn't load organizations</p>
          <p className="text-red-600 text-sm mt-1">{error}</p>
          <button
            onClick={fetchData}
            className="mt-4 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors min-h-[44px]"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const orgs = data?.orgs || data?.organizations || [];

  return (
    <div className="space-y-6">
      {/* Breadcrumb + Header */}
      <div className="flex items-center justify-between">
        <div>
          {/* Breadcrumb */}
          <nav className="flex items-center gap-1.5 text-sm text-gray-500 mb-2">
            <button
              onClick={() => navigate('/onboarding')}
              className="hover:text-gray-900 min-h-[44px] flex items-center"
            >
              Onboarding
            </button>
            <ChevronRightIcon className="w-3.5 h-3.5" />
            <button
              onClick={() => navigate('/onboarding/funnels')}
              className="hover:text-gray-900 min-h-[44px] flex items-center"
            >
              Funnels
            </button>
            <ChevronRightIcon className="w-3.5 h-3.5" />
            <span className="text-gray-900 font-medium">{stepLabel}</span>
          </nav>

          {/* Back + Title */}
          <button
            onClick={() => navigate('/onboarding/funnels')}
            className="flex items-center gap-2 text-gray-600 hover:text-gray-900 mb-2 min-h-[44px]"
          >
            <ArrowLeftIcon className="w-4 h-4" />
            <span className="text-sm">Back to Funnels</span>
          </button>

          {loading ? (
            <div className="h-8 w-64 bg-gray-200 rounded animate-pulse" />
          ) : (
            <div className="flex items-center gap-3 flex-wrap">
              <h2 className="text-2xl font-bold text-gray-900">
                Orgs at Step
              </h2>
              <span className="px-3 py-1 rounded-full text-sm font-medium bg-indigo-100 text-indigo-800">
                {stepLabel}
              </span>
            </div>
          )}
        </div>
        <RefreshIndicator onRefresh={fetchData} loading={loading} />
      </div>

      {/* Summary stat */}
      {!loading && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4 flex items-center gap-3">
          <div className="p-2 bg-indigo-50 rounded-lg">
            <BuildingOffice2Icon className="w-5 h-5 text-indigo-600" />
          </div>
          <p className="text-gray-700">
            <span className="font-semibold text-gray-900">{orgs.length}</span>{' '}
            {orgs.length === 1 ? 'organization has' : 'organizations have'} reached this step
            {nextStepLabel && (
              <span className="text-gray-500">
                {' '}(next up: {nextStepLabel})
              </span>
            )}
          </p>
        </div>
      )}

      {/* Orgs Table */}
      <DataTable
        data={orgs}
        columns={columns}
        loading={loading}
        onRowClick={(org) =>
          navigate(`/onboarding/orgs/${org.id || org.orgId}`)
        }
        emptyMessage="No organizations at this step yet."
      />
    </div>
  );
}
